import { gatewayClient, createService } from './connectors/httpClient';
import ProxyServerConnector from './connectors/ProxyServerConnector';

const transcribesHttpClient = gatewayClient('/transcribes');

function buildTranscribeStatusStreamPath(transcribeJobId: number): string {
    const gatewayPrefix = ProxyServerConnector.gatewayPrefix || '/gateway';

    if (!gatewayPrefix.startsWith('/')) {
        throw new Error('INVALID_GATEWAY_PREFIX');
    }

    const gatewayPath = gatewayPrefix.replace(/\/$/, '');

    return `${gatewayPath}/transcribes/${transcribeJobId}/status/stream`;
}

export type TranscribeStatus = 'COMPLETED' | 'RENDER_FAILED' | 'TRANSCRIPTION_FAILED';

const transcribeService = {
    ...createService(transcribesHttpClient, (client) => ({
        createTranscribeJob(data: { elements: unknown[]; duration: number; languageCode?: string }) {
            return client.post<{ id: number; status: 'PENDING' | 'RENDERED' | 'FAILED'; message?: string }>(
                `/spec`,
                data,
            );
        },

        createVariationTranscribeJob(data: { variationId: string; languageCode?: string }) {
            return client.post<{ id: number; status: 'PENDING' | 'RENDERED' | 'FAILED'; message?: string }>(
                `/variation`,
                data,
            );
        },

        getTranscribeUrl(transcribeJobId: number) {
            return client.get<{ signedUrl?: string; expiresAt?: number }>(`/${transcribeJobId}/url`);
        },
    })),

    listenToTranscribeStatus(
        transcribeJobId: number,
        onStatus: (status: TranscribeStatus) => void,
        onError: (error: Event) => void,
    ): () => void {
        const streamPath = buildTranscribeStatusStreamPath(transcribeJobId);
        const abortController = new AbortController();

        const parseSseChunk = (chunk: string) => {
            const blocks = chunk.split(/\n\n+/);

            blocks.forEach((block) => {
                if (!block.trim()) {
                    return;
                }

                const lines = block.split('\n');
                let eventType = '';
                const dataLines: string[] = [];

                lines.forEach((line) => {
                    if (line.startsWith('event:')) {
                        eventType = line.slice(6).trim();
                    } else if (line.startsWith('data:')) {
                        dataLines.push(line.slice(5).trim());
                    }
                });

                if (eventType && eventType !== 'status') {
                    return;
                }

                const dataText = dataLines.join('\n');

                if (!dataText) {
                    return;
                }

                try {
                    const data = JSON.parse(dataText);
                    const status: TranscribeStatus = data?.status ?? data;

                    onStatus(status);

                    if (status === 'COMPLETED' || status === 'RENDER_FAILED' || status === 'TRANSCRIPTION_FAILED') {
                        abortController.abort();
                    }
                } catch {
                    // ignore malformed messages
                }
            });
        };

        const token = ProxyServerConnector.getToken();
        const headers: HeadersInit = {
            Accept: 'text/event-stream',
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        fetch(streamPath, {
            method: 'GET',
            headers,
            credentials: 'same-origin',
            signal: abortController.signal,
        })
            .then(async (response) => {
                if (!response.ok || !response.body) {
                    throw new Error(`SSE_REQUEST_FAILED_${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    // eslint-disable-next-line no-await-in-loop
                    const { done, value } = await reader.read();

                    if (done) {
                        break;
                    }

                    buffer += decoder.decode(value, { stream: true });
                    const parts = buffer.split(/\n\n+/);
                    buffer = parts.pop() || '';

                    parts.forEach(parseSseChunk);
                }

                if (buffer.trim()) {
                    parseSseChunk(buffer);
                }
            })
            .catch((error) => {
                if (abortController.signal.aborted) {
                    return;
                }

                onError(error as unknown as Event);
            });

        return () => abortController.abort();
    },
};

export default transcribeService;
